import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../../contexts";
import {
  AUDIENCE_SHOW,
  AUDIENCE_GET_ACTIVITY_DETIALS,
  AUDIENCE_SEND_PATH_NUDGE,
} from "../../../endpoints";
import Loader from "../../../organisms/loader/Loader";
import AttributesTab from "./AttributesTab";
import ActivityTab from "./ActivityTab";
import AnswersTab from "./AnswersTab";
import ResponsesTab from "./ResponsesTab";
import FilesTab from "./FilesTab";
import "./Profile.scss";

const TABS = [
  { id: "attributes", label: "Attributes" },
  { id: "activity", label: "Activity" },
  { id: "answers", label: "Answers" },
  { id: "responses", label: "Responses" },
  { id: "files", label: "Files" },
];

// Same no-red status language as the audience table (BL-061).
const STATUS_CLASS = {
  Engaged: "eng",
  Neutral: "neu",
  "Not engaged": "dis",
  "At Risk": "risk",
  "At risk": "risk",
};

const AVATAR_COLORS = ["#C0407A", "#3B7DD8", "#5B54C0", "#8452C0", "#3AA6A0", "#E8843C", "#0FA3B8"];

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// BL-062 — the header actions (Send check-in / Reassign path / •••) are hidden
// until their real functionality is built. Flip to true once wired.
// See "Staging — Deferred Setup & Cost Notes.md" §4 for the intended behavior.
const SHOW_HEADER_ACTIONS = false;

function colorFor(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[Math.abs(h)];
}

function ProfilePage() {
  const { companySlug, id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("attributes");
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [responseFilter, setResponseFilter] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(AUDIENCE_SHOW(id), { headers: { Authorization: token } })
      .then(({ data }) => { if (alive) setProfile(data); })
      .catch(() => { if (alive) toast.error("Could not load this learner."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, token]);

  useEffect(() => {
    let alive = true;
    setActivityLoading(true);
    axios
      .get(AUDIENCE_GET_ACTIVITY_DETIALS(id), { headers: { Authorization: token } })
      .then(({ data }) => { if (alive) setActivityData(data); })
      .catch(() => {})
      .finally(() => { if (alive) setActivityLoading(false); });
    return () => { alive = false; };
  }, [id, token]);

  // "Send check-in" nudges the first path/step that's actually sendable
  // (reuses the existing manual-nudge flow). Disabled when there's no target.
  const checkInTarget = useMemo(() => {
    const paths = activityData?.paths?.data || [];
    for (let i = 0; i < paths.length; i += 1) {
      const p = paths[i];
      if (!p?.nudge?.can_send) continue;
      const step = (p?.nudge?.steps || []).find((s) => s.can_send);
      if (step) return { path: p, step };
    }
    return null;
  }, [activityData]);

  const backToAudience = () => navigate(`/${companySlug}/audiences`);

  const openResponsesFor = (material) => {
    setResponseFilter(material);
    setActiveTab("responses");
  };

  const sendCheckIn = async () => {
    if (!checkInTarget) { toast.info("No active path to check in on yet."); return; }
    setCheckingIn(true);
    try {
      await axios.post(
        AUDIENCE_SEND_PATH_NUDGE(id, checkInTarget.path.learning_material_id),
        { step_uid: checkInTarget.step.step_uid },
        { headers: { Authorization: token } },
      );
      toast.success(`Check-in sent for ${checkInTarget.path.material_name || "current path"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not send check-in.");
    } finally {
      setCheckingIn(false);
    }
  };

  const comingSoon = (label) => toast.info(`${label} — coming soon`);

  if (loading) {
    return (
      <div className="learner-profile">
        <div className="lp-loading"><Loader /></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="learner-profile">
        <div className="lp-empty">
          <p>We couldn&rsquo;t find this learner.</p>
          <button type="button" className="lp-btn" onClick={backToAudience}>Back to Audience</button>
        </div>
      </div>
    );
  }

  const name = profile.learner?.name || "Visitor";
  const statusCls = STATUS_CLASS[profile.activityStatus] || "neu";

  return (
    <div className="learner-profile">
      <ToastContainer position="bottom-right" autoClose={3500} />
      <div className="lp-card">
        <div className="lp-breadcrumb">
          <span className="lp-breadcrumb__back" onClick={backToAudience} role="button" tabIndex={0}>&lsaquo; Audience</span>
          <span className="lp-breadcrumb__sep">/</span>
          <span className="lp-breadcrumb__name">{name}</span>
          <span className="lp-breadcrumb__grow" />
          <span className="lp-breadcrumb__url">/audience/{profile.userId || profile.id}</span>
        </div>

        <div className="lp-head">
          <span className="lp-avatar" style={{ background: colorFor(name) }}>{getInitials(name)}</span>
          <div className="lp-head__id">
            <div className="lp-head__name">
              {name}
              <span className={`au-status au-status--${statusCls}`}>
                <span className="au-status__dot" />{profile.activityStatus}
              </span>
            </div>
            <div className="lp-head__email">{profile.learner?.email || "no email captured"}</div>
          </div>
          <span className="lp-head__grow" />
          {SHOW_HEADER_ACTIONS && (
            <div className="lp-head__actions">
              <button
                type="button"
                className="lp-btn"
                onClick={sendCheckIn}
                disabled={checkingIn || !checkInTarget}
                title={checkInTarget ? "" : "No active path to check in on"}
              >
                {checkingIn ? "Sending…" : "Send check-in"}
              </button>
              <button type="button" className="lp-btn" onClick={() => comingSoon("Reassign path")}>Reassign path</button>
              <button type="button" className="lp-btn lp-btn--icon" onClick={() => comingSoon("More actions")} aria-label="More actions">&bull;&bull;&bull;</button>
            </div>
          )}
        </div>

        <div className="lp-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`lp-tab${activeTab === t.id ? " lp-tab--active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="lp-body">
          {activeTab === "attributes" && (
            <AttributesTab
              profile={profile}
              token={token}
              onSaved={(patch) => setProfile((p) => ({ ...p, ...patch }))}
            />
          )}
          {activeTab === "activity" && (
            <ActivityTab data={activityData} loading={activityLoading} onView={openResponsesFor} />
          )}
          {activeTab === "answers" && <AnswersTab audienceId={id} token={token} />}
          {activeTab === "responses" && (
            <ResponsesTab
              audienceId={id}
              token={token}
              materialFilter={responseFilter}
              onClearFilter={() => setResponseFilter(null)}
            />
          )}
          {activeTab === "files" && <FilesTab audienceId={id} token={token} />}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
